import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthTokens } from '@/types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[];
  // Permisos de plataforma (VIDA-347): '*' del super_admin o platform:* del
  // «Equipo técnico VIDA». Van aparte porque `permissions` se reescribe con los
  // de la organización seleccionada.
  platformPermissions: string[];

  // 2FA transient state (not persisted)
  pendingTwoFactor: boolean;
  preAuthToken: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens) => void;
  setPermissions: (permissions: string[]) => void;
  setPlatformPermissions: (permissions: string[]) => void;
  hasPlatformPermission: (permission: string) => boolean;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  setPendingTwoFactor: (pending: boolean, token?: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      permissions: [],
      platformPermissions: [],
      pendingTwoFactor: false,
      preAuthToken: null,

      setUser: (user) => set({ user }),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        }),

      setPermissions: (permissions) => set({ permissions }),

      setPlatformPermissions: (platformPermissions) => set({ platformPermissions }),

      hasPlatformPermission: (permission) => {
        const { platformPermissions } = get();
        return platformPermissions.includes('*') || platformPermissions.includes(permission);
      },

      login: (user, tokens) =>
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          permissions: [],
          platformPermissions: [],
          pendingTwoFactor: false,
          preAuthToken: null,
        });
        // Clear localStorage
        localStorage.removeItem('vida-auth');
      },

      setLoading: (loading) => set({ isLoading: loading }),

      hasPermission: (permission) => {
        const { permissions } = get();
        if (permissions.includes('*')) return true;
        return permissions.includes(permission);
      },

      hasAnyPermission: (requiredPermissions) => {
        const { permissions } = get();
        if (permissions.includes('*')) return true;
        return requiredPermissions.some((p) => permissions.includes(p));
      },

      setPendingTwoFactor: (pending, token) =>
        set({ pendingTwoFactor: pending, preAuthToken: token || null }),
    }),
    {
      name: 'vida-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
        platformPermissions: state.platformPermissions,
      }),
    }
  )
);
